import { useRef } from 'react'
import { Button, List, Progress, Space, Typography, Upload } from 'antd'
import {
  FolderAddOutlined,
  InboxOutlined,
  UploadOutlined,
} from '@ant-design/icons'
import {
  enumerateWorkflowDrop,
  enumerateWorkflowFiles,
} from '../../utils/enumerate'
import type { FileUploaderProps } from './types'
import styles from './index.module.css'

/**
 * Selects or drops files and folders of any type, and lists each queued
 * attempt's progress with a retry action for failures.
 *
 * Dragger presents the drop target; the workflow controller owns uploads.
 */
function FileUploader({
  entries,
  disabled,
  onAdd,
  onRetry,
  onError,
}: FileUploaderProps) {
  const files = useRef<HTMLInputElement>(null)
  const folders = useRef<HTMLInputElement | null>(null)
  const folderSupported = 'webkitdirectory' in document.createElement('input')

  /** Queues an input's selected files, then clears it so reselection fires. */
  const select = (input: HTMLInputElement) => {
    try {
      onAdd(enumerateWorkflowFiles(Array.from(input.files ?? [])))
    } catch (error) {
      onError(error)
    }
    input.value = ''
  }

  return (
    <section aria-label="Upload files">
      <div
        className={styles.uploadArea}
        onDragOver={(event) => event.preventDefault()}
      >
        {/* Keep our enumeration: Dragger's directory traversal skips empty folders. */}
        <Upload.Dragger
          multiple
          disabled={disabled}
          openFileDialogOnClick={false}
          showUploadList={false}
          beforeUpload={() => Upload.LIST_IGNORE}
          onDrop={async (event) => {
            event.preventDefault()
            if (disabled) return
            try {
              onAdd(await enumerateWorkflowDrop(event.dataTransfer.items))
            } catch (error) {
              onError(error)
            }
          }}
        >
          <p className="ant-upload-drag-icon">
            <InboxOutlined />
          </p>
          <p className="ant-upload-text">
            Drag and drop files or folders of any type here
          </p>
          <Space className={styles.uploadActions} wrap>
            <Button
              aria-label="Choose files"
              icon={<UploadOutlined />}
              disabled={disabled}
              onClick={() => files.current?.click()}
            >
              Choose files
            </Button>
            <Button
              aria-label="Choose folders"
              icon={<FolderAddOutlined />}
              disabled={disabled || !folderSupported}
              onClick={() => folders.current?.click()}
            >
              Choose folders
            </Button>
          </Space>
          {!folderSupported && (
            <Typography.Paragraph>
              Folder selection is unavailable in this browser. Choose files, or
              use a browser with directory support.
            </Typography.Paragraph>
          )}
        </Upload.Dragger>
        <input
          ref={files}
          type="file"
          multiple
          hidden
          aria-label="Select files"
          onChange={(event) => select(event.currentTarget)}
        />
        <input
          // React's input typings omit the nonstandard directory attribute.
          ref={(element) => {
            folders.current = element
            element?.setAttribute('webkitdirectory', '')
          }}
          type="file"
          multiple
          hidden
          aria-label="Select folders"
          onChange={(event) => select(event.currentTarget)}
        />
      </div>
      {entries.length > 0 && (
        <List
          aria-label="Upload queue"
          className={styles.uploadQueue}
          size="small"
          dataSource={entries}
          renderItem={(entry) => {
            const pending =
              entry.state === 'queued' || entry.state === 'uploading'
            const uploaded =
              entry.state === 'uploaded' || entry.state === 'replaced'
            let percent = 0
            if (uploaded) percent = 100
            else if (entry.file?.size) {
              // Sending every byte to the API does not confirm a SharePoint commit.
              const sent = Math.max(entry.offset, entry.sentOffset ?? 0)
              percent = Math.min(99, Math.floor((sent / entry.file.size) * 100))
            }
            const retryable =
              entry.state === 'failed' ||
              entry.state === 'unverified' ||
              entry.state === 'conflict' ||
              entry.state === 'cancelled'
            return (
              <List.Item
                actions={[
                  retryable ? (
                    <Button
                      key="retry"
                      size="small"
                      disabled={disabled}
                      onClick={() => onRetry(entry.id)}
                      aria-label={`Retry ${entry.path.join('/')}`}
                    >
                      Retry
                    </Button>
                  ) : null,
                ].filter(Boolean)}
              >
                <Space
                  className={styles.queueItem}
                  direction="vertical"
                  size={4}
                >
                  <Typography.Text>{entry.path.join('/')}</Typography.Text>
                  <Typography.Text aria-live="polite">
                    {uploaded
                      ? 'Uploaded'
                      : entry.state.charAt(0).toUpperCase() +
                        entry.state.slice(1)}
                  </Typography.Text>
                  {(pending || uploaded) && entry.file && (
                    <Progress
                      size="small"
                      percent={percent}
                      format={(value) => `${value}%`}
                    />
                  )}
                  {entry.message && entry.state !== 'cancelled' && (
                    <Typography.Text type="secondary">
                      {entry.message}
                    </Typography.Text>
                  )}
                </Space>
              </List.Item>
            )
          }}
        />
      )}
    </section>
  )
}
export default FileUploader
